import { Component, OnInit, inject } from '@angular/core';
import {JobService} from '../../shared/services/job.service';
import {StudentService} from '../../shared/services/student.service';
import {CompanyService} from '../../shared/services/company.service';
import {ModalService} from '../../shared/services/modal.service';
import {AlertService, alertMessages} from '../../shared/services/alert.service';
import {UserService} from '../../shared/services/user.service';
import {RoleTypes} from '../../guards/roletypes';
import {ModalAdminStudentprofileComponent} from '../admin-studentprofiles/modal-admin-studentprofile/modal-admin-studentprofile.component';

@Component({
    selector: 'app-admin-dashboard',
    templateUrl: './admin-dashboard.component.html',
    styleUrls: ['./admin-dashboard.component.scss'],
    standalone: false
})
export class AdminDashboardComponent implements OnInit {

  private readonly userService = inject(UserService);

  /**
   * Dashboard widget for Companies is visible for every admin role. The
   * underlying API call switches per role:
   *   Beheerder                       → GET /company/all  (apiGetCompaniesAll)
   *   ProfessionalAdmin / InternationalAdmin → GET /company (apiGetCompanies)
   * `/company/all` returns 403 for the sub-admin roles, so they use the regular
   * GET endpoint which they are allowed to call.
   */
  readonly canManageCompanies = this.userService.currentUserIsAdmin();
  private readonly canViewAllCompanies =
    this.userService.currentUserHasRole(RoleTypes.Beheerder);

  view = {

    jobs: {
      data: <any> [],
      isLoading: false,
      totalItems: 0
    },
    companies: {
      data: <any> [],
      isLoading: false,
      totalItems: 0
    },
    studentprofiles: {
      data: <any> [],
      isLoading: false,
      totalItems: 0
    }
  }

  /**
   * Constructor
   *
   * @param jobService
   * @param studentService
   * @param companyService
   * @param modalService
   * @param alertService
   */
  constructor(
    public jobService: JobService,
    public studentService: StudentService,
    public companyService: CompanyService,
    private modalService: ModalService,
    private alertService: AlertService
  ) { }

  /**
   * Ng on init
   */
  ngOnInit(): void {

    if (this.canManageCompanies) {
      this.loadCompanies();
    }
    this.loadStudentprofiles();
    this.loadJobs();


  }

  /**
   * Load job posting data
   */
  loadJobs() {

    const $params = {
      pageSize: 5,
      pageNumber: 1,
      SortField: 'createdOn',
      SortType: 'Descending'
    };

    this.view.jobs.isLoading = true;
    this.jobService.apiGetJobPostings($params).subscribe({
      next: (response: any) => {
        this.view.jobs.isLoading = false;
        if (response.hasOwnProperty('data')) {
          this.view.jobs.data = response.data;
        }
        if (response.hasOwnProperty('totalItems')) {
          this.view.jobs.totalItems = response.totalItems;
        }
      },
      error: () => {
        this.view.jobs.isLoading = false;
      },
    });
  }

  /**
   * Load student profile data
   */
  loadStudentprofiles() {

    const $params = {
      pageSize: 5,
      pageNumber: 1,
      SortField: 'createdOn',
      SortType: 'Descending'
    };

    this.view.studentprofiles.isLoading = true;
    this.studentService.apiGetStudentProfiles($params).subscribe({
      next: (response: any) => {
        this.view.studentprofiles.isLoading = false;
        if (response.hasOwnProperty('data')) {
          this.view.studentprofiles.data = response.data;
        }
        if (response.hasOwnProperty('totalItems')) {
          this.view.studentprofiles.totalItems = response.totalItems;
        }
      },
      error: () => {
        this.view.studentprofiles.isLoading = false;
      },
    });
  }


  /**
   * Load company data
   */
  loadCompanies() {

    const $params = {
      pageSize: 5,
      pageNumber: 1,
      SortField: 'createdOn',
      SortType: 'Descending'
    };

    this.view.companies.isLoading = true;
    const companies$ = this.canViewAllCompanies
      ? this.companyService.apiGetCompaniesAll($params)
      : this.companyService.apiGetCompanies($params);
    companies$.subscribe({
      next: (response: any) => {
        this.view.companies.isLoading = false;
        if (response.hasOwnProperty('data')) {
          this.view.companies.data = response.data;
        }
        if (response.hasOwnProperty('totalItems')) {
          this.view.companies.totalItems = response.totalItems;
        }
      },
      error: () => {
        this.view.companies.isLoading = false;
      },
    });
  }

  /**
   * Open student profile modal
   *
   * @param studentData
   */
  openStudentProfileModal(studentData: any) {
    const modalOptions = {
      backdrop: 'static',
      size: 'xl'
    };

    const data = {
      entity: studentData
    };

    this.modalService.Modal(ModalAdminStudentprofileComponent, '', '', data, [], modalOptions).then((result) => {
      if (result === true) {
        this.alertService.success(alertMessages.successDataSaved);
        // Reload student profiles data
        this.loadStudentprofiles();
      }
    }).catch((error) => {
      // Handle modal dismissal
    });
  }

}
